import React, { useState } from 'react';
import logoImage from '../assets/logo_h_small.png';

const APK_URL = process.env.REACT_APP_DRIVER_APP_URL;
const APP_VERSION = process.env.REACT_APP_DRIVER_APK_VERSION || '1.5';
const APP_SIZE = process.env.REACT_APP_DRIVER_APK_SIZE || '14 MB';

const features = [
  {
    icon: (
      <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'Commandes en temps réel',
    desc: 'Recevez les courses instantanément et naviguez vers le client sans friction.',
  },
  {
    icon: (
      <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z" fill="currentColor"/>
      </svg>
    ),
    title: 'Navigation intégrée',
    desc: 'GPS optimisé pour les rues locales avec itinéraires mis à jour en continu.',
  },
  {
    icon: (
      <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
        <path d="M12 20V10M6 20V16M18 20v-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Suivi des gains',
    desc: 'Vos revenus, courses et performances en un seul coup d\'œil.',
  },
  {
    icon: (
      <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'Paiements sécurisés',
    desc: 'Recevez vos paiements de façon transparente et sans délai.',
  },
];

const steps = [
  {
    num: '1',
    title: 'Téléchargez l\'APK',
    desc: 'Cliquez sur le bouton de téléchargement pour récupérer le fichier sur votre téléphone.',
  },
  {
    num: '2',
    title: 'Autorisez l\'installation',
    desc: 'Dans Paramètres → Sécurité, activez « Sources inconnues » puis lancez le fichier APK.',
  },
  {
    num: '3',
    title: 'Connectez-vous et livrez',
    desc: 'Utilisez vos identifiants livreur OuiGoo et commencez à recevoir des courses.',
  },
];

const DownloadIcon = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
    <path d="M12 16l-4-4h2.5V4h3v8H16l-4 4z" fill="currentColor"/>
    <path d="M4 18h16v2H4v-2z" fill="currentColor"/>
  </svg>
);

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
  </svg>
);

const DriverAppDownload = () => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = () => {
    setDownloading(true);
    const link = document.createElement('a');
    link.href = APK_URL;
    link.download = APK_URL.split('/').pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => setDownloading(false), 3000);
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans overflow-x-hidden">

      {/* Header */}
      <header className="px-6 py-4 border-b border-gray-100">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="/">
            <img src={logoImage} alt="OuiGoo" className="h-6" />
          </a>
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Application Livreur</span>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 pt-20 pb-24 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* Left */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-50 border border-orange-100 text-sm font-medium text-orange-600">
              <span className="w-2 h-2 rounded-full bg-[#F89133] animate-pulse" />
              Version {APP_VERSION} disponible
            </div>

            <h1 className="text-5xl md:text-6xl font-bold leading-[1.08] tracking-tight text-gray-900">
              Livrez plus.<br />
              Gagnez plus.<br />
              <span className="text-[#F89133]">Sans friction.</span>
            </h1>

            <p className="text-lg text-gray-500 leading-relaxed max-w-md">
              L'application OuiGoo Livreur vous donne tous les outils pour gérer vos courses, suivre vos gains et livrer efficacement, où que vous soyez.
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row items-start gap-5">
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="flex items-center gap-3 px-7 py-4 rounded-2xl bg-[#F89133] text-white font-bold text-base transition-all duration-200 hover:bg-[#e07620] active:scale-[0.97] disabled:opacity-70 shadow-lg shadow-orange-200 hover:shadow-orange-300"
              >
                {downloading ? <><SpinnerIcon /> Téléchargement…</> : <><DownloadIcon /> Télécharger l'APK</>}
              </button>

              <div className="flex flex-col justify-center gap-1.5 text-sm text-gray-400">
                <span className="flex items-center gap-2">
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2"/></svg>
                  Sécurisé &amp; vérifié
                </span>
                <span className="flex items-center gap-2">
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/><path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                  {APP_SIZE} · Android 8+
                </span>
              </div>
            </div>

            <p className="text-xs text-gray-300 flex items-center gap-2">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.523 15.341l1.043-1.806a.5.5 0 0 0-.866-.5l-1.057 1.83A7.975 7.975 0 0 0 12 13.5a7.975 7.975 0 0 0-4.643 1.365L6.3 13.035a.5.5 0 0 0-.866.5l1.043 1.806A8 8 0 0 0 4 21h16a8 8 0 0 0-2.477-5.659zM8.5 19a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm7 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2zM12 4l1.5-2.598h-3L12 4z"/>
              </svg>
              Fichier APK Android uniquement. iOS non disponible pour le moment.
            </p>
          </div>

          {/* Right — Phone mockup */}
          <div className="flex justify-center lg:justify-end">
            <div className="relative w-[260px]">
              {/* Shadow glow */}
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-48 h-12 bg-orange-200 blur-2xl rounded-full opacity-60 pointer-events-none" />
              {/* Phone shell */}
              <div className="relative bg-gray-900 border border-gray-800 rounded-[44px] px-4 pt-5 pb-5 shadow-2xl">
                {/* Notch */}
                <div className="w-20 h-5 bg-black rounded-full mx-auto mb-4" />
                {/* Screen */}
                <div className="bg-[#111] rounded-[32px] overflow-hidden h-[430px] flex flex-col">
                  {/* App header */}
                  <div className="px-5 pt-6 pb-3">
                    <div className="flex items-center justify-between mb-5">
                      <div>
                        <p className="text-[10px] text-white/40">Bonjour,</p>
                        <p className="text-sm font-bold text-white">Kofi A. 👋</p>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-[#F89133]/20 flex items-center justify-center">
                        <span className="text-[#F89133] text-xs font-bold">K</span>
                      </div>
                    </div>
                    {/* Status */}
                    <div className="flex items-center gap-2 bg-[#F89133]/10 border border-[#F89133]/20 rounded-xl px-3 py-2.5">
                      <span className="w-2 h-2 rounded-full bg-[#F89133] animate-pulse" />
                      <span className="text-xs text-[#F89133] font-semibold">En ligne · Disponible</span>
                    </div>
                  </div>
                  {/* Stats */}
                  <div className="px-5 grid grid-cols-2 gap-2 mb-4">
                    {[['12', 'Livraisons'], ['18 500', 'FCFA']].map(([v, l]) => (
                      <div key={l} className="bg-white/5 rounded-xl p-3">
                        <p className="text-sm font-bold text-white">{v}</p>
                        <p className="text-[9px] text-white/40 mt-0.5">{l}</p>
                      </div>
                    ))}
                  </div>
                  {/* Order card */}
                  <div className="mx-5 bg-[#F89133] rounded-2xl p-4 flex flex-col gap-1 shadow-lg shadow-orange-900/30">
                    <div className="flex items-center justify-between">
                      <p className="text-[9px] font-bold text-black/50 uppercase tracking-wider">Nouvelle course</p>
                      <span className="text-[9px] bg-black/10 px-2 py-0.5 rounded-full font-semibold text-black/60">+1 400 FCFA</span>
                    </div>
                    <p className="text-xs font-bold text-black mt-0.5">Cotonou, Cadjèhoun</p>
                    <p className="text-[9px] text-black/50">3.2 km · 8 min estimés</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-6xl mx-auto px-6">
        <div className="h-px bg-gray-100" />
      </div>

      {/* Features */}
      <section className="px-6 py-24 max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl font-bold text-gray-900">Conçu pour les professionnels</h2>
          <p className="text-gray-400 mt-3">Tout ce dont vous avez besoin pour livrer efficacement.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f) => (
            <div
              key={f.title}
              className="group rounded-2xl border border-gray-100 p-6 hover:border-orange-100 hover:shadow-md hover:shadow-orange-50 transition-all duration-200 bg-white"
            >
              <div className="w-11 h-11 rounded-xl bg-orange-50 text-[#F89133] flex items-center justify-center mb-5 group-hover:bg-orange-100 transition-colors">
                {f.icon}
              </div>
              <h3 className="font-semibold text-gray-900 text-sm mb-2">{f.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-6xl mx-auto px-6">
        <div className="h-px bg-gray-100" />
      </div>

      {/* Steps */}
      <section className="px-6 py-24 max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl font-bold text-gray-900">3 étapes pour démarrer</h2>
          <p className="text-gray-400 mt-3">L'installation prend moins de 2 minutes.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((s) => (
            <div key={s.num} className="relative flex gap-5 p-7 rounded-2xl border border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm transition-all duration-200">
              <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-[#F89133] text-white font-black text-base flex items-center justify-center shadow-md shadow-orange-200">
                {s.num}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1.5">{s.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Banner */}
      <section className="px-6 pb-24 max-w-6xl mx-auto">
        <div className="relative overflow-hidden rounded-3xl bg-gray-900 px-12 py-16 text-center">
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '28px 28px'}}
          />
          <div className="relative">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-3 tracking-tight">
              Prêt à livrer avec OuiGoo ?
            </h2>
            <p className="text-white/50 mb-10 text-base">
              Rejoignez notre réseau de livreurs dès aujourd'hui.
            </p>
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#F89133] text-white rounded-2xl font-bold text-base transition-all duration-200 hover:bg-[#e07620] active:scale-[0.97] disabled:opacity-70 shadow-lg shadow-orange-900/30"
            >
              {downloading
                ? <><SpinnerIcon /> Téléchargement…</>
                : <><DownloadIcon /> Télécharger l'APK · {APP_SIZE}</>
              }
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 px-6 py-8">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-400">
          <span>© {new Date().getFullYear()} OuiGoo · Application Livreur v{APP_VERSION}</span>
          <div className="flex items-center gap-6">
            <a href="/driver-privacy-policy" className="hover:text-gray-700 transition-colors">Confidentialité</a>
            <a href="/driver-terms-of-service" className="hover:text-gray-700 transition-colors">Conditions</a>
            <a href="/contact" className="hover:text-gray-700 transition-colors">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DriverAppDownload;
